import { Widget } from "@rjsf/core";

type WidgetObj = { [type: string]: Widget };

export enum EWidgetType { Read = "read", Write = "write" }

export interface IBuildWidgets {
    read: WidgetObj;
    write: WidgetObj;
}

/**
 * A class instance which is used as a helper object for combining multiple JSONSchema
 * widgets for both data writing ("form") and reading ("display"). Widgets can be added
 * to a registry instance with the addWidget and appendWidget function. Once all widgets
 * are added, the build-function can be called and the widget lists can be verified.
 *
 * @export
 * @class WidgetRegistry
 * @example
 * const registry = WidgetRegistry.create();
 *
 * registry.appendWidgets(EWidgetType.Read, defaultReadWidgets);
 * registry.appendWidgets(EWidgetType.Write, defaultWriteWidgets);
 *
 * registry.addWidget(EWidgetType.Read, "customWidget", CustomWidgetView);
 * registry.addWidget(EWidgetType.Write, "customWidget", CustomWidgetForm);
 *
 * const { read, write } = registry.build();
 */
export class WidgetRegistry {

    private readWidgets: WidgetObj = {};
    private writeWidgets: WidgetObj = {};

    private constructor() { }

    /**
     * Creates a new instance of WidgetRegistry, accepts no parameters
     */
    public static create(): WidgetRegistry {
        return new WidgetRegistry();
    }

    /**
     * Adds a new widget to the widget registry. Does not check the name, allowing
     * overwrites to happen.
     * @param type Is this a read or a write widget? Use the EWidgetType enum here
     * @param name Name of the widget, the same you use in the schema
     * @param widget The widget component
     */
    public addWidget(type: EWidgetType, name: string, widget: Widget) {
        if (type == EWidgetType.Read) {
            this.readWidgets[name] = widget;
        } else {
            this.writeWidgets[name] = widget;
        }
    }

    /**
     * Append widgets as an object. Useful if you already have a set of widgets in an object
     * @param type Read or write widgets?
     * @param widgets String-mapped object where values are Widget components
     */
    public appendWidgets(type: EWidgetType, widgets: WidgetObj) {
        const names = Object.keys(widgets);
        const append = (obj: WidgetObj) => {
            names.forEach((name) => {
                obj[name] = widgets[name];
            });
        };

        if (type === EWidgetType.Read) {
            append(this.readWidgets);
        } else {
            append(this.writeWidgets);
        }
    }

    /**
     * Verify and build the widget registry.
     * Returns a copy of the current registries for both read and write widgets.
     * @throws Throws an error if any of the widgets does not have a matching read/write pair.
     */
    public build() {
        const writeNames = Object.keys(this.readWidgets);
        const readNames = Object.keys(this.writeWidgets);

        const check = (crossCheckArr: string[], type: EWidgetType) => (name: string) => {
            const isInCrossCheckList = crossCheckArr.indexOf(name) !== -1;
            if (!isInCrossCheckList) {
                const isWrite = type === EWidgetType.Write;
                throw new Error(`[CustomWidgetsError]: A widget called '${name}' was defined in ${isWrite ? "write" : "read"} widgets, but not in ${isWrite ? "read" : "write"} widgets`);
            }
        };

        writeNames.forEach(check(readNames, EWidgetType.Write));
        readNames.forEach(check(writeNames, EWidgetType.Read));

        return {
            read: { ...this.readWidgets },
            write: { ...this.writeWidgets },
        };
    }
}
